import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ArrowLeft, Plus, Pencil, Trash2, Building2, Users, Download, Calendar,
    Loader2, X, Save, Search, RefreshCw,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_BADGE = {
    planifie: { label: 'Planifié', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    en_cours: { label: 'En cours', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    termine: { label: 'Terminé', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const MOIS_NOMS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const EMPTY_PRESTATION = {
    entreprise_id: '',
    date: new Date().toISOString().slice(0, 10),
    heure_debut: '08:00',
    heure_fin: '17:00',
    adresse_prestation: '',
    demenageurs: [],
    mode_tarif: 'forfait',
    tarif_forfait_jour: 0,
    tarif_horaire: 0,
    statut: 'planifie',
    remarque: '',
};

export default function MiseADispo() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const navigate = useNavigate();

    const [entreprises, setEntreprises] = useState([]);
    const [staff, setStaff] = useState([]);
    const [prestations, setPrestations] = useState([]);

    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterEntId, setFilterEntId] = useState('');
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());

    // Entreprise modal
    const [entOpen, setEntOpen] = useState(false);
    const [entEdit, setEntEdit] = useState(null);
    const [entForm, setEntForm] = useState({ nom: '', contact_nom: '', telephone: '', email: '', adresse: '', siret: '', remarque: '' });

    // Prestation modal
    const [presOpen, setPresOpen] = useState(false);
    const [presEditId, setPresEditId] = useState(null);
    const [presForm, setPresForm] = useState(EMPTY_PRESTATION);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [eRes, sRes, pRes] = await Promise.all([
                axios.get(`${API_URL}/api/mise-a-dispo/entreprises`, { withCredentials: true }),
                axios.get(`${API_URL}/api/demenageurs-staff`, { withCredentials: true }),
                axios.get(`${API_URL}/api/mise-a-dispo/prestations`, { withCredentials: true }),
            ]);
            setEntreprises(eRes.data || []);
            setStaff(sRes.data || []);
            setPrestations(pRes.data || []);
        } catch {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ----------------------------------------------------------------------
    // Entreprises
    // ----------------------------------------------------------------------
    const openEntreprise = (e = null) => {
        if (e) {
            setEntEdit(e.id);
            setEntForm({
                nom: e.nom, contact_nom: e.contact_nom || '', telephone: e.telephone || '',
                email: e.email || '', adresse: e.adresse || '', siret: e.siret || '', remarque: e.remarque || '',
            });
        } else {
            setEntEdit(null);
            setEntForm({ nom: '', contact_nom: '', telephone: '', email: '', adresse: '', siret: '', remarque: '' });
        }
        setEntOpen(true);
    };

    const saveEntreprise = async () => {
        if (!entForm.nom.trim()) { toast.error('Nom requis'); return; }
        try {
            if (entEdit) {
                await axios.put(`${API_URL}/api/mise-a-dispo/entreprises/${entEdit}`, entForm, { withCredentials: true });
                toast.success('Entreprise modifiée');
            } else {
                await axios.post(`${API_URL}/api/mise-a-dispo/entreprises`, entForm, { withCredentials: true });
                toast.success('Entreprise créée');
            }
            setEntOpen(false);
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        }
    };

    const deleteEntreprise = async (id, nom) => {
        if (!window.confirm(`Supprimer définitivement "${nom}" ?\n\nLes prestations existantes resteront affichées mais ne pourront plus être créées sur cette entreprise.`)) return;
        try {
            await axios.delete(`${API_URL}/api/mise-a-dispo/entreprises/${id}`, { withCredentials: true });
            toast.success('Entreprise supprimée');
            fetchAll();
        } catch { toast.error('Erreur'); }
    };

    // ----------------------------------------------------------------------
    // Prestations
    // ----------------------------------------------------------------------
    const openPrestation = async (id = null) => {
        if (id) {
            try {
                const r = await axios.get(`${API_URL}/api/mise-a-dispo/prestations/${id}`, { withCredentials: true });
                setPresForm({ ...EMPTY_PRESTATION, ...r.data });
                setPresEditId(id);
            } catch { toast.error('Erreur chargement'); return; }
        } else {
            setPresEditId(null);
            setPresForm({ ...EMPTY_PRESTATION, entreprise_id: filterEntId || (entreprises[0]?.id || '') });
        }
        setPresOpen(true);
    };

    const savePrestation = async () => {
        if (!presForm.entreprise_id) { toast.error('Entreprise requise'); return; }
        if (!presForm.date) { toast.error('Date requise'); return; }
        if (!presForm.demenageurs || presForm.demenageurs.length === 0) {
            toast.error('Au moins un déménageur requis');
            return;
        }
        const payload = {
            ...presForm,
            tarif_forfait_jour: Number(presForm.tarif_forfait_jour) || 0,
            tarif_horaire: Number(presForm.tarif_horaire) || 0,
        };
        try {
            if (presEditId) {
                await axios.put(`${API_URL}/api/mise-a-dispo/prestations/${presEditId}`, payload, { withCredentials: true });
                toast.success('Prestation modifiée');
            } else {
                await axios.post(`${API_URL}/api/mise-a-dispo/prestations`, payload, { withCredentials: true });
                toast.success('Prestation créée');
            }
            setPresOpen(false);
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        }
    };

    const deletePrestation = async (id) => {
        if (!window.confirm('Supprimer cette prestation ?')) return;
        try {
            await axios.delete(`${API_URL}/api/mise-a-dispo/prestations/${id}`, { withCredentials: true });
            toast.success('Prestation supprimée');
            fetchAll();
        } catch { toast.error('Erreur'); }
    };

    const addDemenageurInternal = (staffMember) => {
        if (presForm.demenageurs.some(d => d.staff_id === staffMember.id)) return;
        setPresForm({
            ...presForm,
            demenageurs: [...presForm.demenageurs, {
                staff_id: staffMember.id,
                nom: `${staffMember.prenom || ''} ${staffMember.nom || ''}`.trim(),
                externe: false,
                chef_equipe: false,
            }],
        });
    };

    const addDemenageurExterne = () => {
        const nom = window.prompt('Nom du déménageur externe :');
        if (!nom?.trim()) return;
        setPresForm({
            ...presForm,
            demenageurs: [...presForm.demenageurs, {
                staff_id: '',
                nom: nom.trim(),
                externe: true,
                chef_equipe: false,
            }],
        });
    };

    const removeDemenageur = (idx) => {
        setPresForm({
            ...presForm,
            demenageurs: presForm.demenageurs.filter((_, i) => i !== idx),
        });
    };

    const setChefEquipe = (idx) => {
        setPresForm({
            ...presForm,
            demenageurs: presForm.demenageurs.map((d, i) => ({ ...d, chef_equipe: i === idx })),
        });
    };

    // ----------------------------------------------------------------------
    // PDF download
    // ----------------------------------------------------------------------
    const downloadPDF = async (entreprise) => {
        toast.loading('Génération PDF…', { id: 'mad-pdf' });
        try {
            const res = await axios.get(
                `${API_URL}/api/mise-a-dispo/entreprises/${entreprise.id}/pointage-pdf?month=${month}&year=${year}`,
                { responseType: 'blob', withCredentials: true }
            );
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `Pointage_${entreprise.nom.replace(/\s/g, '_')}_${year}-${String(month).padStart(2, '0')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 500);
            toast.success('PDF téléchargé', { id: 'mad-pdf' });
        } catch {
            toast.error('Erreur PDF', { id: 'mad-pdf' });
        }
    };

    // ----------------------------------------------------------------------
    // Filtering
    // ----------------------------------------------------------------------
    const filteredPrestations = useMemo(() => {
        const s = search.toLowerCase().trim();
        return prestations.filter((p) => {
            if (filterEntId && p.entreprise_id !== filterEntId) return false;
            if (!s) return true;
            return (
                (p.entreprise_nom || '').toLowerCase().includes(s) ||
                (p.adresse_prestation || '').toLowerCase().includes(s) ||
                (p.demenageurs || []).some((d) => (d.nom || '').toLowerCase().includes(s))
            );
        });
    }, [prestations, search, filterEntId]);

    const monthTotal = useMemo(() => {
        return filteredPrestations.reduce((sum, p) => sum + (p.total || 0), 0);
    }, [filteredPrestations]);

    if (!isAdmin) {
        return <Layout><div className="p-8 text-center text-gray-500">Accès admin requis.</div></Layout>;
    }

    return (
        <Layout>
            <div className="space-y-5" data-testid="mise-a-dispo-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-[#1B365D]">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Mise à disposition
                            </h1>
                            <p className="text-xs text-gray-500">Suivi du pointage des manutentionnaires loués à d'autres entreprises.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => openEntreprise()}
                            className="bg-[#1B365D] hover:bg-[#152a4a] text-white gap-1.5"
                            data-testid="mad-add-entreprise-btn"
                        >
                            <Building2 className="w-4 h-4" /> + Entreprise
                        </Button>
                        <Button
                            onClick={() => openPrestation()}
                            disabled={entreprises.length === 0}
                            className="bg-[#C9A962] hover:bg-[#b89854] text-white gap-1.5"
                            data-testid="mad-add-prestation-btn"
                        >
                            <Plus className="w-4 h-4" /> + Prestation
                        </Button>
                        <button
                            onClick={fetchAll}
                            className="text-sm text-[#1B365D] hover:bg-gray-100 px-3 py-1.5 rounded-lg gap-1.5 inline-flex items-center"
                            data-testid="mad-refresh"
                        >
                            <RefreshCw className="w-4 h-4" /> Rafraîchir
                        </button>
                    </div>
                </div>

                {/* Entreprises sidebar / list */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {/* Entreprises */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 lg:col-span-1">
                        <h2 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5" /> Entreprises clientes ({entreprises.length})
                        </h2>
                        <div className="space-y-1 max-h-[400px] overflow-y-auto">
                            <button
                                onClick={() => setFilterEntId('')}
                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                    filterEntId === '' ? 'bg-[#1B365D] text-white' : 'hover:bg-gray-50 text-gray-700'
                                }`}
                                data-testid="mad-filter-all"
                            >
                                Toutes
                            </button>
                            {entreprises.map((e) => (
                                <div
                                    key={e.id}
                                    className={`group flex items-center gap-1 rounded-md transition-colors ${
                                        filterEntId === e.id ? 'bg-[#1B365D] text-white' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                    data-testid={`mad-entreprise-${e.id}`}
                                >
                                    <button
                                        onClick={() => setFilterEntId(e.id)}
                                        className="flex-1 text-left px-3 py-2 text-sm truncate"
                                    >
                                        {e.nom}
                                    </button>
                                    <button
                                        onClick={() => openEntreprise(e)}
                                        className={`opacity-0 group-hover:opacity-100 p-1 transition-opacity ${
                                            filterEntId === e.id ? 'text-white hover:bg-white/20' : 'text-gray-400 hover:text-blue-600'
                                        }`}
                                        title="Modifier"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => deleteEntreprise(e.id, e.nom)}
                                        className={`opacity-0 group-hover:opacity-100 p-1 mr-1 transition-opacity ${
                                            filterEntId === e.id ? 'text-white hover:bg-white/20' : 'text-gray-400 hover:text-red-600'
                                        }`}
                                        title="Supprimer"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                            {entreprises.length === 0 && (
                                <p className="text-center text-xs text-gray-400 py-6 italic">
                                    Aucune entreprise. Cliquez sur "+ Entreprise" pour commencer.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Prestations list */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 lg:col-span-3">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <div className="relative flex-1 min-w-[180px]">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Rechercher…"
                                    className="h-8 pl-7 text-sm"
                                    data-testid="mad-search"
                                />
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                                <Calendar className="w-3.5 h-3.5 text-gray-500" />
                                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                                    <SelectTrigger className="h-8 text-xs w-[110px]" data-testid="mad-month"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {MOIS_NOMS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Input
                                    type="number"
                                    value={year}
                                    onChange={(e) => setYear(Number(e.target.value))}
                                    className="h-8 text-xs w-[80px]"
                                    data-testid="mad-year"
                                />
                            </div>
                            {filterEntId && (
                                <Button
                                    onClick={() => {
                                        const e = entreprises.find((x) => x.id === filterEntId);
                                        if (e) downloadPDF(e);
                                    }}
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 gap-1.5"
                                    data-testid="mad-download-pdf-btn"
                                >
                                    <Download className="w-3.5 h-3.5" /> PDF mensuel
                                </Button>
                            )}
                        </div>

                        {/* Stats card */}
                        <div className="bg-gradient-to-r from-[#1B365D]/5 to-[#C9A962]/10 rounded-lg p-3 mb-3 flex items-center justify-between border border-[#C9A962]/30">
                            <div>
                                <span className="text-xs text-gray-500">
                                    {filteredPrestations.length} prestation(s){filterEntId && entreprises.find((e) => e.id === filterEntId) ? ` chez ${entreprises.find((e) => e.id === filterEntId).nom}` : ''}
                                </span>
                            </div>
                            <div className="text-right">
                                <span className="text-xs text-gray-500 block">Total HT cumulé</span>
                                <span className="text-lg font-bold text-[#1B365D]" data-testid="mad-total-cumul">
                                    {monthTotal.toFixed(2)} €
                                </span>
                            </div>
                        </div>

                        {/* Table */}
                        {loading ? (
                            <div className="text-center py-10 text-gray-400">
                                <Loader2 className="w-5 h-5 animate-spin inline" />
                            </div>
                        ) : filteredPrestations.length === 0 ? (
                            <div className="text-center text-gray-400 py-10 italic">
                                Aucune prestation. Cliquez sur "+ Prestation" pour en créer une.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 tracking-wide">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-semibold">Entreprise</th>
                                            <th className="text-left px-3 py-2 font-semibold">Date</th>
                                            <th className="text-left px-3 py-2 font-semibold">Horaire</th>
                                            <th className="text-left px-3 py-2 font-semibold">Déménageurs</th>
                                            <th className="text-left px-3 py-2 font-semibold">Tarif</th>
                                            <th className="text-right px-3 py-2 font-semibold">Total HT</th>
                                            <th className="text-left px-3 py-2 font-semibold">Statut</th>
                                            <th className="text-right px-3 py-2 font-semibold">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredPrestations.map((p) => {
                                            const st = STATUT_BADGE[p.statut] || STATUT_BADGE.planifie;
                                            const chef = (p.demenageurs || []).find((d) => d.chef_equipe);
                                            return (
                                                <tr key={p.id} className="hover:bg-gray-50/80" data-testid={`mad-prestation-row-${p.id}`}>
                                                    <td className="px-3 py-2 font-medium text-gray-900">{p.entreprise_nom || '—'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{p.date}</td>
                                                    <td className="px-3 py-2 text-gray-600">{p.heure_debut} → {p.heure_fin} <span className="text-[10px] text-gray-400">({p.heures.toFixed(1)}h)</span></td>
                                                    <td className="px-3 py-2 text-gray-700">
                                                        <span className="font-semibold">{p.n_demenageurs}</span>
                                                        {chef && <span className="text-[10px] text-gray-500 block">Chef : {chef.nom}</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-600 text-xs">
                                                        {p.mode_tarif === 'forfait'
                                                            ? `${p.tarif_forfait_jour.toFixed(0)} €/jr/p`
                                                            : `${p.tarif_horaire.toFixed(2)} €/h/p`}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-semibold text-[#1B365D]">{p.total.toFixed(2)} €</td>
                                                    <td className="px-3 py-2">
                                                        <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                                                    </td>
                                                    <td className="px-3 py-2 text-right">
                                                        <div className="inline-flex items-center gap-1">
                                                            <button
                                                                onClick={() => openPrestation(p.id)}
                                                                className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                                                                title="Modifier"
                                                                data-testid={`mad-edit-${p.id}`}
                                                            >
                                                                <Pencil className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={() => deletePrestation(p.id)}
                                                                className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                                                title="Supprimer"
                                                                data-testid={`mad-delete-${p.id}`}
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- Entreprise modal --- */}
                <Dialog open={entOpen} onOpenChange={setEntOpen}>
                    <DialogContent className="max-w-lg" data-testid="mad-entreprise-modal">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" /> {entEdit ? 'Modifier' : 'Nouvelle'} entreprise</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Nom <span className="text-red-500">*</span></Label>
                                <Input value={entForm.nom} onChange={(e) => setEntForm({ ...entForm, nom: e.target.value })} data-testid="mad-ent-nom" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Contact</Label>
                                    <Input value={entForm.contact_nom} onChange={(e) => setEntForm({ ...entForm, contact_nom: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Téléphone</Label>
                                    <Input value={entForm.telephone} onChange={(e) => setEntForm({ ...entForm, telephone: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={entForm.email} onChange={(e) => setEntForm({ ...entForm, email: e.target.value })} />
                            </div>
                            <div>
                                <Label>Adresse</Label>
                                <Input value={entForm.adresse} onChange={(e) => setEntForm({ ...entForm, adresse: e.target.value })} />
                            </div>
                            <div>
                                <Label>SIRET</Label>
                                <Input value={entForm.siret} onChange={(e) => setEntForm({ ...entForm, siret: e.target.value })} />
                            </div>
                            <div>
                                <Label>Remarque</Label>
                                <Textarea rows={2} value={entForm.remarque} onChange={(e) => setEntForm({ ...entForm, remarque: e.target.value })} />
                            </div>
                            <div className="flex justify-end gap-2 pt-2 border-t">
                                <Button variant="outline" onClick={() => setEntOpen(false)}>Annuler</Button>
                                <Button onClick={saveEntreprise} className="bg-[#1B365D] hover:bg-[#152a4a] text-white gap-1.5" data-testid="mad-ent-save"><Save className="w-4 h-4" />Enregistrer</Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* --- Prestation modal --- */}
                <Dialog open={presOpen} onOpenChange={setPresOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mad-prestation-modal">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> {presEditId ? 'Modifier' : 'Nouvelle'} prestation</DialogTitle>
                            <DialogDescription>Loue tes manutentionnaires à une autre entreprise.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            {/* Entreprise + date */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="md:col-span-1">
                                    <Label>Entreprise <span className="text-red-500">*</span></Label>
                                    <Select value={presForm.entreprise_id} onValueChange={(v) => setPresForm({ ...presForm, entreprise_id: v })}>
                                        <SelectTrigger data-testid="mad-pres-ent"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                                        <SelectContent>
                                            {entreprises.map((e) => <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Date <span className="text-red-500">*</span></Label>
                                    <Input type="date" value={presForm.date} onChange={(e) => setPresForm({ ...presForm, date: e.target.value })} data-testid="mad-pres-date" />
                                </div>
                                <div>
                                    <Label>Statut</Label>
                                    <Select value={presForm.statut} onValueChange={(v) => setPresForm({ ...presForm, statut: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="planifie">Planifié</SelectItem>
                                            <SelectItem value="en_cours">En cours</SelectItem>
                                            <SelectItem value="termine">Terminé</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Heures + adresse */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <Label>Heure début</Label>
                                    <Input type="time" value={presForm.heure_debut} onChange={(e) => setPresForm({ ...presForm, heure_debut: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Heure fin</Label>
                                    <Input type="time" value={presForm.heure_fin} onChange={(e) => setPresForm({ ...presForm, heure_fin: e.target.value })} />
                                </div>
                                <div className="col-span-2">
                                    <Label>Lieu</Label>
                                    <Input value={presForm.adresse_prestation} onChange={(e) => setPresForm({ ...presForm, adresse_prestation: e.target.value })} placeholder="Adresse de la prestation" />
                                </div>
                            </div>

                            {/* Déménageurs */}
                            <div>
                                <Label className="flex items-center justify-between">
                                    <span>Déménageurs ({presForm.demenageurs.length})</span>
                                    <button type="button" onClick={addDemenageurExterne} className="text-xs text-[#1B365D] hover:underline" data-testid="mad-pres-add-externe">+ Ajouter un externe</button>
                                </Label>
                                <div className="border border-gray-200 rounded-lg p-2 space-y-2">
                                    {/* Selected list */}
                                    {presForm.demenageurs.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {presForm.demenageurs.map((d, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
                                                        d.chef_equipe ? 'bg-amber-100 text-amber-800 border-amber-300' :
                                                        d.externe ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-gray-100 text-gray-700 border-gray-300'
                                                    }`}
                                                    data-testid={`mad-pres-dem-${idx}`}
                                                >
                                                    {d.chef_equipe && <span className="font-bold">★</span>}
                                                    {d.externe && <span className="text-[9px] font-bold">EXT</span>}
                                                    {d.nom}
                                                    <button onClick={() => setChefEquipe(idx)} className="text-amber-600 hover:text-amber-800" title="Définir comme chef d'équipe">★</button>
                                                    <button onClick={() => removeDemenageur(idx)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Internal team picker */}
                                    <div className="border-t pt-2">
                                        <p className="text-[10px] uppercase font-bold text-gray-400 mb-1">Équipe interne</p>
                                        <div className="flex flex-wrap gap-1">
                                            {staff.map((s) => {
                                                const taken = presForm.demenageurs.some((d) => d.staff_id === s.id);
                                                return (
                                                    <button
                                                        key={s.id}
                                                        type="button"
                                                        onClick={() => !taken && addDemenageurInternal(s)}
                                                        disabled={taken}
                                                        className={`px-2 py-0.5 rounded-md text-xs border ${
                                                            taken
                                                                ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed line-through'
                                                                : 'bg-white text-gray-700 border-gray-200 hover:bg-[#1B365D] hover:text-white hover:border-[#1B365D]'
                                                        }`}
                                                        data-testid={`mad-pres-staff-${s.id}`}
                                                    >
                                                        {`${s.prenom || ''} ${s.nom || ''}`.trim()}
                                                    </button>
                                                );
                                            })}
                                            {staff.length === 0 && (
                                                <span className="text-xs text-gray-400 italic">Aucun déménageur dans l'équipe (ajoute-en via "Gérer l'équipe")</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tarification */}
                            <div className="bg-amber-50/40 border border-amber-200 rounded-lg p-3">
                                <Label className="text-xs font-bold uppercase text-amber-800">Tarification</Label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                                    <div>
                                        <Label>Mode</Label>
                                        <Select value={presForm.mode_tarif} onValueChange={(v) => setPresForm({ ...presForm, mode_tarif: v })}>
                                            <SelectTrigger data-testid="mad-pres-mode"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="forfait">Forfait journée</SelectItem>
                                                <SelectItem value="horaire">Tarif horaire</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {presForm.mode_tarif === 'forfait' ? (
                                        <div>
                                            <Label>Forfait €/jour/personne</Label>
                                            <Input type="number" step="0.01" value={presForm.tarif_forfait_jour} onChange={(e) => setPresForm({ ...presForm, tarif_forfait_jour: e.target.value })} data-testid="mad-pres-forfait" />
                                        </div>
                                    ) : (
                                        <div>
                                            <Label>Taux €/heure/personne</Label>
                                            <Input type="number" step="0.01" value={presForm.tarif_horaire} onChange={(e) => setPresForm({ ...presForm, tarif_horaire: e.target.value })} data-testid="mad-pres-horaire" />
                                        </div>
                                    )}
                                    <div>
                                        <Label>Total estimé HT</Label>
                                        <div className="h-9 px-3 flex items-center bg-white border border-gray-200 rounded-md font-bold text-[#1B365D]" data-testid="mad-pres-total-preview">
                                            {(() => {
                                                const n = presForm.demenageurs.length;
                                                if (n === 0) return '0.00 €';
                                                if (presForm.mode_tarif === 'forfait') return `${(Number(presForm.tarif_forfait_jour || 0) * n).toFixed(2)} €`;
                                                const [h1, m1] = (presForm.heure_debut || '08:00').split(':').map(Number);
                                                const [h2, m2] = (presForm.heure_fin || '17:00').split(':').map(Number);
                                                const heures = Math.max(((h2 * 60 + m2) - (h1 * 60 + m1)) / 60, 0);
                                                return `${(Number(presForm.tarif_horaire || 0) * heures * n).toFixed(2)} €`;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Label>Remarque</Label>
                                <Textarea rows={2} value={presForm.remarque} onChange={(e) => setPresForm({ ...presForm, remarque: e.target.value })} />
                            </div>

                            <div className="flex justify-end gap-2 pt-2 border-t">
                                <Button variant="outline" onClick={() => setPresOpen(false)}>Annuler</Button>
                                <Button onClick={savePrestation} className="bg-[#C9A962] hover:bg-[#b89854] text-white gap-1.5" data-testid="mad-pres-save"><Save className="w-4 h-4" />Enregistrer</Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
